import { useContext, useMemo } from 'react';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { TIMESHEET_GROUP_BY_OPTIONS_ENUM } from '@ubeya/shared/constants';
import { useRecoilValue } from 'recoil';
import { selectedBranches } from '@ubeya/shared/atoms/shared';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import useJobTypes from '@ubeya/shared/hooks/generic/useJobTypes';
import usePtoTypes from '@ubeya/shared/hooks/generic/usePtoTypes';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import useAdmins from '@ubeya/shared/hooks/account/useAdmins';
import useTags from '@ubeya/shared/hooks/account/useTags';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useTimesheetFields from '@ubeya/shared/hooks/account/useTimesheetFields';
import { TIMESHEET_TABLE_CONTEXT } from 'shared/constants/timeSheet';
import {
  useApproveColumn,
  useNameColumn,
  useDateAsNameColumn,
  useInColumn,
  useOutColumn,
  useCheckInColumn,
  useCheckOutColumn,
  useTotalColumn,
  useTotalWithoutBreaksColumn,
  useShiftColumn,
  useShiftStartTimeColumn,
  useShiftEndTimeColumn,
  useDifferenceColumn,
  useShiftAreaColumn,
  useBranchColumn,
  useDateColumn,
  usePositionColumn,
  useCostCenterColumn,
  useClientNameColumn,
  useTagsColumn,
  useBreaksColumn,
  useDetailsColumn,
  useExpandedRatingColumn,
  useReviewOnEmployeeWithExternalColumn,
  useGroupingColumns,
  useClientApprovedColumn,
  useEmployeeApprovedColumn,
  useApprovedByColumn,
  useApprovedAtColumn,
  useShiftDateColumn,
  useTimesheetFieldsColumns,
  useLockedTimesheetColumn,
  useProcessColumn,
  useMainEventColumn,
  useTimesheetPayrollFieldsColumns
} from './columns';

export const useTimeSheetGridColumns = ({
  totalRows,
  setShiftModal,
  approveTimeSheet,
  showDateColumn = false,
  updateStartTime,
  updateEndTime,
  updateCheckInTime,
  updateCheckOutTime,
  hasActionPermission,
  setSelectedEmployee,
  showCheckInOutColumns,
  showShiftColumn = true,
  showEmployeeSignoffColumn,
  showTagsColumn = true,
  groupBy,
  updateBreak,
  deleteBreak,
  isBreakTimesDisplayed,
  updateEmployeeRating,
  updateReviewOnEmployee,
  showTimesheetFields = false,
  setIsPromptOpen = () => {},
  tableContext,
  showLockedTimesheetColumn = false,
  clickableDate = false,
  showEmployeeRating = false,
  updateOSClientApproval,
  updateOSStartTime,
  updateOSEndTime,
  updateOSCheckInTime,
  updateOSCheckOutTime,
  updateOSBreaks,
  deleteOSBreak,
  updateOSEmployeeRating,
  updateOSReviewOnEmployee,
  refetchTimesheet,
  showPayrollProcessFeature = false,
  mappedSupplierBranches,
  updateTimesheetTaskField,
  amplitudeModuleName = 'Timesheet module',
  showMainEventColumn = false,
  payrollFields,
  updatePayrollField
}) => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const { mappedBranches, branchesOptions } = useBranches();
  const { mappedLabels } = useLabels();
  const { mappedJobTypes } = useJobTypes();
  const { mappedLocations } = useLocations();
  const { mappedPtoTypes } = usePtoTypes();
  const { mappedPositions } = usePositions();
  const { mappedAdmins } = useAdmins();
  const { mappedTags } = useTags();
  const { fields: timesheetFields } = useTimesheetFields();
  const selectedBranchesValue = useRecoilValue(selectedBranches);
  const showBranchColumn = (branchesOptions || []).every(({ value }) => (selectedBranchesValue || []).includes(value));
  const showDateColumnInBasic = [
    TIMESHEET_GROUP_BY_OPTIONS_ENUM.POSITION,
    TIMESHEET_GROUP_BY_OPTIONS_ENUM.PROJECT,
    TIMESHEET_GROUP_BY_OPTIONS_ENUM.CLIENT,
    TIMESHEET_GROUP_BY_OPTIONS_ENUM.MAIN_EVENT
  ].includes(groupBy);

  const isEmployeeJobTab = tableContext === TIMESHEET_TABLE_CONTEXT.EMPLOYEE_JOB_TAB;
  const hasOSHandlers = !!(
    updateOSClientApproval ||
    updateOSStartTime ||
    updateOSEndTime ||
    updateOSCheckInTime ||
    updateOSCheckOutTime ||
    updateOSBreaks ||
    deleteOSBreak ||
    updateOSEmployeeRating ||
    updateOSReviewOnEmployee
  );

  const approveColumn = useApproveColumn({
    approveTimeSheet,
    editOSTimeSheet: updateOSClientApproval,
    mappedSupplierBranches,
    suppressAggregate: isEmployeeJobTab
  });
  const processColumn = useProcessColumn();
  const lockedTimesheetColumn = useLockedTimesheetColumn();
  const clientApprovedColumn = useClientApprovedColumn();
  const employeeApprovedColumn = useEmployeeApprovedColumn();
  const shiftDateColumn = useShiftDateColumn();
  const shiftDateColumnNextToName = useShiftDateColumn({ nextToName: true });
  const dateAsNameColumn = useDateAsNameColumn({ accountId, clickableDate });
  const nameColumn = useNameColumn({
    totalRows,
    groupBy,
    accountId,
    setSelectedEmployee,
    showCaptain: showEmployeeRating,
    showRating: showEmployeeRating,
    showAvatar: true
  });
  const inColumn = useInColumn({
    updateStartTime,
    updateEndTime,
    hasActionPermission,
    setIsPromptOpen,
    updateOSStartTime,
    updateOSEndTime,
    allowUpdateExternal: hasOSHandlers
  });
  const outColumn = useOutColumn({
    updateEndTime,
    hasActionPermission,
    setIsPromptOpen,
    updateOSEndTime,
    allowUpdateExternal: hasOSHandlers
  });
  const checkInColumn = useCheckInColumn({
    updateCheckInTime,
    updateCheckOutTime,
    hasActionPermission,
    updateOSCheckInTime,
    updateOSCheckOutTime,
    allowUpdateExternal: hasOSHandlers
  });
  const checkOutColumn = useCheckOutColumn({
    updateCheckOutTime,
    hasActionPermission,
    updateOSCheckOutTime,
    allowUpdateExternal: hasOSHandlers
  });
  const totalColumn = useTotalColumn({
    updateStartTime,
    updateEndTime,
    hasActionPermission,
    updateOSStartTime,
    updateOSEndTime,
    allowUpdateExternal: hasOSHandlers
  });
  const totalWithoutBreaksColumn = useTotalWithoutBreaksColumn();
  const shiftColumn = useShiftColumn({
    accountId,
    mappedLabels,
    mappedJobTypes,
    mappedLocations,
    mappedPtoTypes
  });
  const shiftStartTimeColumn = useShiftStartTimeColumn();
  const shiftEndTimeColumn = useShiftEndTimeColumn();
  const differenceColumn = useDifferenceColumn();
  const shiftAreaColumn = useShiftAreaColumn({ mappedLocations });
  const branchColumn = useBranchColumn({ mappedBranches });
  const dateColumn = useDateColumn();
  const positionColumn = usePositionColumn({ mappedPositions });
  const costCenterColumn = useCostCenterColumn({ groupBy });
  const clientNameColumn = useClientNameColumn();
  const tagsColumn = useTagsColumn({ mappedTags });
  const breaksColumn = useBreaksColumn({
    updateBreak,
    deleteBreak,
    hasActionPermission,
    updateOSBreaks,
    deleteOSBreak,
    allowUpdateExternal: hasOSHandlers
  });
  const approvedByColumn = useApprovedByColumn({ mappedAdmins });
  const approvedAtColumn = useApprovedAtColumn();
  const detailsColumn = useDetailsColumn({ setShiftModal });
  const ratingColumn = useExpandedRatingColumn({
    onChangeRating: updateEmployeeRating,
    onExternalChangeRating: updateOSEmployeeRating,
    onEmployeeStatusChange: refetchTimesheet,
    mappedSupplierBranches
  });
  const reviewOnEmployeeColumn = useReviewOnEmployeeWithExternalColumn({
    onUpdate: updateReviewOnEmployee,
    onExternalUpdate: updateOSReviewOnEmployee,
    readOnly: !updateReviewOnEmployee && !updateOSReviewOnEmployee
  });
  const timesheetFieldsColumns = useTimesheetFieldsColumns({
    showTimesheetFields,
    timesheetFields,
    updateTimesheetTaskField,
    amplitudeModuleName
  });
  const groupingColumns = useGroupingColumns({ groupBy });
  const mainEventColumn = useMainEventColumn();
  const payrollFieldsColumns = useTimesheetPayrollFieldsColumns({ payrollFields, updatePayrollField });

  return useMemo(() => {
    const approvalColumnsGroup = {
      groupId: 'approval-group',
      headerName: t('approval'),
      marryChildren: true,
      children: [
        approveColumn,
        ...(showLockedTimesheetColumn ? [lockedTimesheetColumn] : []),
        ...(showPayrollProcessFeature ? [processColumn] : []),
        approvedByColumn,
        approvedAtColumn,
        clientApprovedColumn,
        ...(showEmployeeSignoffColumn ? [employeeApprovedColumn] : [])
      ]
    };

    const schedulingColumnsGroup = {
      groupId: 'scheduling-group',
      headerName: t('scheduling'),
      marryChildren: true,
      children: [
        shiftStartTimeColumn,
        shiftEndTimeColumn,
        inColumn,
        ...(showCheckInOutColumns ? [checkInColumn, checkOutColumn] : []),
        outColumn,
        totalColumn,
        totalWithoutBreaksColumn,
        differenceColumn,
        ...(isBreakTimesDisplayed ? [breaksColumn] : [])
      ]
    };

    const basicColumnsGroup = {
      groupId: 'basic-group',
      headerName: t('basic'),
      marryChildren: true,
      children: [
        ...(showDateColumn ? (groupBy !== TIMESHEET_GROUP_BY_OPTIONS_ENUM.NO_GROUP_BY ? [shiftDateColumn] : []) : []),
        ...(showDateColumnInBasic ? [dateColumn] : []),
        ...(groupBy !== TIMESHEET_GROUP_BY_OPTIONS_ENUM.POSITION ? [positionColumn] : []),
        ...(showShiftColumn ? [shiftColumn] : []),
        ...(showBranchColumn ? [branchColumn] : []),
        ...(showMainEventColumn ? [mainEventColumn] : []),
        shiftAreaColumn,
        costCenterColumn,
        clientNameColumn,
        ...(showTagsColumn ? [tagsColumn] : []),
        ratingColumn,
        reviewOnEmployeeColumn,
        detailsColumn
      ]
    };

    const timesheetFieldsColumnsGroup = {
      groupId: 'timesheetFields-group',
      headerName: t('timesheetFields'),
      marryChildren: true,
      children: timesheetFieldsColumns
    };

    const payrollFieldsColumnsGroup = {
      groupId: 'payrollFields-group',
      headerName: t('payroll'),
      marryChildren: true,
      children: payrollFieldsColumns
    };

    const columnDefs = [
      {
        field: 'id',
        hide: true,
        suppressColumnsToolPanel: true
      },
      approvalColumnsGroup,
      ...(groupBy === TIMESHEET_GROUP_BY_OPTIONS_ENUM.NO_GROUP_BY ? [shiftDateColumnNextToName] : []),
      isEmployeeJobTab ? dateAsNameColumn : nameColumn,
      schedulingColumnsGroup,
      basicColumnsGroup,
      ...(timesheetFieldsColumns.length > 0 ? [timesheetFieldsColumnsGroup] : []),
      ...(payrollFieldsColumns.length > 0 ? [payrollFieldsColumnsGroup] : []),
      ...groupingColumns
    ];
    return columnDefs;
  }, [
    t,
    showEmployeeSignoffColumn,
    clientApprovedColumn,
    approvedByColumn,
    approvedAtColumn,
    employeeApprovedColumn,
    showDateColumn,
    shiftDateColumn,
    shiftDateColumnNextToName,
    inColumn,
    showCheckInOutColumns,
    checkInColumn,
    checkOutColumn,
    outColumn,
    totalColumn,
    totalWithoutBreaksColumn,
    showShiftColumn,
    shiftColumn,
    shiftStartTimeColumn,
    shiftEndTimeColumn,
    shiftAreaColumn,
    showBranchColumn,
    branchColumn,
    showTagsColumn,
    tagsColumn,
    showDateColumnInBasic,
    dateColumn,
    groupBy,
    positionColumn,
    differenceColumn,
    isBreakTimesDisplayed,
    breaksColumn,
    costCenterColumn,
    clientNameColumn,
    ratingColumn,
    reviewOnEmployeeColumn,
    detailsColumn,
    timesheetFieldsColumns,
    approveColumn,
    showPayrollProcessFeature,
    processColumn,
    showLockedTimesheetColumn,
    lockedTimesheetColumn,
    isEmployeeJobTab,
    dateAsNameColumn,
    nameColumn,
    groupingColumns,
    showMainEventColumn,
    mainEventColumn,
    payrollFieldsColumns
  ]);
};
